
import { CommonDiscountGroupNames } from '@/enums/components/Discount/CommonDiscountGroupNames';
import { fetchAndTransformMaterials } from '@/helpers/Discount/DiscountHelper';
import ProductGroupRepository from '@/repositories/ProductGroupRepository';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { SessionStorageService as ss } from '@/services/SessionStorageService';

@Component({
    name: 'DiscountInformationRow',
})
export default class DiscountInformationRow extends Vue {
    @Prop({ required: true }) private value!: any;
    @Prop({ required: false, default: true }) private canUserEditDiscounts!: boolean;

    private cachedValue: any = [];
    private debounceTimer: any = null;

    private materialDataSource: null | any[] = null;

    private get columns() {
        return [
            {
                title: this.$t('Kategorija'),
                dataIndex: 'discountCategory',
                scopedSlots: { customRender: 'category' },
            },
            {
                title: this.$t('Ime'),
                dataIndex: 'name',
                scopedSlots: { customRender: 'name' },
            },
            {
                title: this.$t('PC1'),
                dataIndex: 'pci1',
                scopedSlots: { customRender: 'pc1' },
            },
            {
                title: this.$t('PC2'),
                dataIndex: 'pci2',
                scopedSlots: { customRender: 'pc2' },
            },
            {
                title: this.$t('Vrsta'),
                dataIndex: 'type',
                width: '10%',
                scopedSlots: { customRender: 'type' },
            },
            {
                title: this.$t('Akcije'),
                dataIndex: 'actions',
                width: '12%',
                scopedSlots: { customRender: 'actions' },
            },
        ];
    }

    private get discountsTableData() {
        if (!this.value || !this.value.length) {
            return [];
        }

        return this.value.map((discount: any, index: number) => ({
            ...discount,
            key: index,
        }));
    }

    private get discountCategories() {
        return [
            {
                value: CommonDiscountGroupNames.PRODUCT,
                text: this.$t(CommonDiscountGroupNames.PRODUCT),
            },
            {
                value: CommonDiscountGroupNames.PRODUCT_GROUP,
                text: this.$t(CommonDiscountGroupNames.PRODUCT_GROUP),
            },
        ];
    }

    private commonDiscountGroupNames = CommonDiscountGroupNames;

    private discountCategoryText(record: any) {
        const index = record.key;

        if (!this.value[index].discountCategory) {
            return record?.name || '';
        }

        return this.discountCategories.find((i) => i.value === this.value[index].discountCategory)?.text || '';
    }

    private entityOidText(record: any) {
        const index = record.key;

        if (this.value[index].productGroup) {
            const productGroup = this.productGroups.find((i) => i.id === this.value[index].productGroup);

            return productGroup?.name || record?.name || '';
        }

        if (this.value[index].discountedEntityOid && this.materialDataSource) {
            const material = this.materialDataSource.find((i) => i.value === this.value[index].discountedEntityOid);

            return material?.text || record?.name || '';
        }

        return '';
    }

    private get productGroups() {
        return ProductGroupRepository.getAll();
    }

    private filteredProductGroups(selectedGroupID: string | null) {
        if (!this.productGroups || !this.productGroups.length) {
            return [];
        }

        const filteredProductGroups = this.productGroups.filter((group) => {
            return this.value.every((discount: any) => {
                if (group.id === selectedGroupID) {
                    return true;
                }

                return group.id !== discount.productGroup;
            });
        });

        return filteredProductGroups;
    }

    private filteredMaterials(materialId: string) {
        if (!this.materialDataSource || !this.materialDataSource.length) {
            return [];
        }

        const filteredMaterials = this.materialDataSource.filter((material) => {
            return this.value.every((discount: any) => {
                if (material.value === materialId) {
                    return true;
                }

                if (discount.discountCategory === this.commonDiscountGroupNames.PRODUCT) {
                    return material.value !== discount.discountedEntityOid;
                }

                return true;
            });
        });

        if (materialId && !filteredMaterials.find((filteredMaterial) => filteredMaterial.value === materialId)) {
            const material = this.value.find((material: any) => material.discountedEntityOid === materialId);
            const formattedMaterial = {
                value: materialId,
                text: material ? material.name : '',
            };

            if (material) {
                filteredMaterials.unshift(formattedMaterial);
            }
        }

        return filteredMaterials;
    }

    private onEdit(index: number) {
        this.value[index].isEditing = true;
        this.isEditingChanged();

        this.cachedValue = [...this.value];
    }

    private confirmChanges(index: number) {
        this.value[index].isEditing = false;
        this.isEditingChanged();
        this.$emit('input', this.value);
        this.$emit('update', index);
    }

    private cancelChanges(index: number) {
        if (!this.cachedValue[index]) {
            this.$emit('delete', index);
            this.isEditingChanged();
            return;
        }
        this.cachedValue[index].isEditing = false;
        this.value[index].isEditing = false;
        this.isEditingChanged();
        this.$emit('input', this.cachedValue);
        this.$emit('cancel', index);
    }

    private isEditingChanged() {
        this.$emit('isEditingChanged');
    }

    private debouncedUpdateMaterials(value: string) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.updateLocallyAndCacheMaterials(value);
        }, 300);
    }

    private handleSelect(value: string) {
        this.value.discountedEntityOid = value;
    }

    private async fetchMaterials() {
        if (this.value.discountCategory === 'PRODUCT_GROUP' || !ss.has('materials')) {
            return;
        }

        this.materialDataSource = ss.getAndParse('materials');

        // Check if cached values contain initial value and fetch it if not
        const isInitialValueCached = this.materialDataSource?.some((currentObj) => {
            return this.value.discountedEntityOid === currentObj.value;
        });

        if (!isInitialValueCached) {
            this.updateLocallyAndCacheMaterials(this.value.name);
        }
    }

    private async updateLocallyAndCacheMaterials(name = '') {
        const materials = await fetchAndTransformMaterials(name);

        this.materialDataSource = [...materials];
        ss.updateUniqueArray('materials', materials);
    }

    private async mounted() {
        await this.fetchMaterials();
    }

    private handleDiscountCategoryChange(index: number) {
        const category = this.value[index].discountCategory;

        if (category) {
            if (category === this.commonDiscountGroupNames.PRODUCT) {
                this.value[index].productGroup = null;
            }

            if (category === this.commonDiscountGroupNames.PRODUCT_GROUP) {
                this.value[index].discountedEntityOid = null;
            }

            this.$emit('input', this.value);
        }
    }
}
